// Luật tự động cho chiến dịch quảng cáo: mô tả bằng lời, kiểm tham số, định dạng số.

export type AdRuleTemplate = 'PauseHighCpc' | 'PauseLowCtr' | 'ScaleUpBudget' | 'ScaleDownBudget';

export interface AdRuleInput {
  template: string;
  windowDays: number;
  threshold: number;
  minImpressions?: number | null;
  changePercent?: number | null;
}

export const RULE_TEMPLATES: { value: AdRuleTemplate; label: string; isBudget: boolean }[] = [
  { value: 'PauseHighCpc', label: 'Tạm dừng khi giá mỗi lượt bấm quá cao', isBudget: false },
  { value: 'PauseLowCtr', label: 'Tạm dừng khi tỉ lệ bấm quá thấp', isBudget: false },
  { value: 'ScaleUpBudget', label: 'Tăng ngân sách ngày khi tỉ lệ bấm tốt', isBudget: true },
  { value: 'ScaleDownBudget', label: 'Giảm ngân sách ngày khi giá mỗi lượt bấm cao', isBudget: true },
];

export const WINDOW_OPTIONS = [1, 3, 7];
export const DEFAULT_MIN_IMPRESSIONS = 500;
export const MAX_RULES_PER_CAMPAIGN = 5;

export const isBudgetTemplate = (t: string) => t === 'ScaleUpBudget' || t === 'ScaleDownBudget';
export const isPauseTemplate = (t: string) => t === 'PauseHighCpc' || t === 'PauseLowCtr';

/** Chiến dịch đã kết thúc (bị từ chối, hết ngân sách, đã dừng hẳn): không đổi luật, ngân sách, nội dung được nữa. */
export const isCampaignEnded = (status?: string | null) =>
  status === 'Rejected' || status === 'Completed' || status === 'Cancelled';

/** Chỉ giữ chữ số khi gõ số Ca (không cho phần lẻ, dấu chấm, dấu trừ). */
export const digitsOnly = (raw: string) => raw.replace(/\D/g, '');

/** Số Ca nguyên từ ô nhập; rỗng hoặc không phải số nguyên dương thì null. */
export const parseWholeCa = (raw: string): number | null => {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return null;
  const v = Number(t);
  return Number.isSafeInteger(v) ? v : null;
};

const nf = new Intl.NumberFormat('vi-VN', { maximumFractionDigits: 2 });

/** Số Ca có dấu chấm ngăn cách hàng nghìn, ví dụ "2.000 Ca". */
export const formatCa = (v?: number | null): string =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : `${nf.format(Math.round(v))} Ca`;

/** Tỉ lệ bấm (đã là phần trăm), ví dụ "3,25%". */
export const formatCtr = (v?: number | null): string =>
  v === null || v === undefined || Number.isNaN(v) ? '—' : `${nf.format(Math.round(v * 100) / 100)}%`;

const windowText = (d: number) => (d === 1 ? 'hôm qua' : `${d} ngày gần nhất`);

export function describeRule(r: AdRuleInput): string {
  const win = windowText(r.windowDays);
  const th = r.threshold;
  const minImp = r.minImpressions ?? DEFAULT_MIN_IMPRESSIONS;
  const pct = r.changePercent ?? 0;
  switch (r.template) {
    case 'PauseHighCpc':
      return `Tạm dừng khi giá mỗi lượt bấm trong ${win} vượt ${formatCa(th)} (sau ít nhất ${nf.format(minImp)} lượt hiển thị)`;
    case 'PauseLowCtr':
      return `Tạm dừng khi tỉ lệ bấm trong ${win} thấp hơn ${formatCtr(th)} (sau ít nhất ${nf.format(minImp)} lượt hiển thị)`;
    case 'ScaleUpBudget':
      return `Tăng ngân sách ngày thêm ${pct}% khi tỉ lệ bấm trong ${win} từ ${formatCtr(th)} trở lên và hôm qua đã tiêu gần hết ngân sách ngày`;
    case 'ScaleDownBudget':
      return `Giảm ngân sách ngày ${pct}% khi giá mỗi lượt bấm trong ${win} vượt ${formatCa(th)}`;
    default:
      return 'Luật không rõ';
  }
}

/** Trả về câu lỗi tiếng Việt, hoặc null nếu hợp lệ. Khớp quy tắc phía máy chủ. */
export function validateRule(r: AdRuleInput, hasDailyBudget: boolean): string | null {
  if (!RULE_TEMPLATES.some((t) => t.value === r.template)) return 'Hãy chọn mẫu luật.';
  if (!WINDOW_OPTIONS.includes(r.windowDays)) return 'Khung thời gian chỉ chọn 1, 3 hoặc 7 ngày.';
  if (!(r.threshold > 0)) return 'Ngưỡng phải lớn hơn 0.';
  if (isPauseTemplate(r.template)) {
    const m = r.minImpressions ?? DEFAULT_MIN_IMPRESSIONS;
    if (!Number.isInteger(m) || m < 100) return 'Số lượt hiển thị tối thiểu từ 100 trở lên.';
  } else {
    const c = r.changePercent;
    if (c === null || c === undefined || !Number.isInteger(c) || c < 5 || c > 50)
      return 'Mức thay đổi ngân sách từ 5% đến 50%.';
    if (!hasDailyBudget) return 'Luật tăng hoặc giảm ngân sách cần chiến dịch có ngân sách ngày.';
  }
  return null;
}

export function actionLabel(action: string): string {
  switch (action) {
    case 'Paused':
      return 'Tạm dừng';
    case 'BudgetIncreased':
      return 'Tăng ngân sách';
    case 'BudgetDecreased':
      return 'Giảm ngân sách';
    default:
      return action;
  }
}

/** Số liệu lúc luật chạy: giá mỗi lượt bấm (Ca) với luật giá, tỉ lệ bấm (%) với luật tỉ lệ. */
export function metricText(template: string, value: number): string {
  return template === 'PauseLowCtr' || template === 'ScaleUpBudget'
    ? `tỉ lệ bấm ${formatCtr(value)}`
    : `giá mỗi lượt bấm ${formatCa(value)}`;
}

export function budgetChangeText(before?: number | null, after?: number | null): string {
  if (before == null && after == null) return '—';
  return `${formatCa(before)} thành ${formatCa(after)}`;
}
